/** Keep newsletter feedback inline without competing with Shopify's CAPTCHA. */
class NewsletterForm extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.form = this.querySelector('form');
    if (!this.form) return;
    if (this.querySelector('[data-newsletter-success]')) this.clearSuccessUrl();

    if (!this.captchaReady) {
      // Shopify wraps the form's submit method when protect() binds it. Install
      // our transport first so CAPTCHA finishes before we read the form data.
      // If another script already bound it, preserve the native form fallback.
      if (this.form.dataset.hcaptchaBound || this.form.dataset.recaptchaBound) return;
      this.form.submit = this.send;
      this.captchaReady = new Promise((resolve) => {
        if (window.Shopify?.captcha?.protect) window.Shopify.captcha.protect(this.form, resolve);
        else resolve();
      });
    }
    this.abort = new AbortController();
    this.form.addEventListener('submit', this.onSubmit, { capture: true, signal: this.abort.signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.abort = null;
    clearTimeout(this.timeout);
    if (this.pending) this.setBusy(false);
    this.pending = false;
    this.request?.abort();
  }

  clearSuccessUrl() {
    const url = new URL(location.href);
    if (url.searchParams.get('customer_posted') !== 'true') return;
    url.searchParams.delete('customer_posted');
    if (url.hash === `#${this.form.id}`) url.hash = 'footer';
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  onSubmit = async (event) => {
    if (event.defaultPrevented) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.pending || this.querySelector('[data-newsletter-success]')) return;
    this.pending = true;
    this.querySelector('[data-newsletter-error]').hidden = true;
    this.setBusy(true);
    this.timeout = setTimeout(() => {
      this.request?.abort();
      this.fail();
    }, 120000);
    try {
      await this.captchaReady;
      if (this.pending) this.form.submit();
    } catch {
      this.fail();
    }
  };

  send = async () => {
    if (!this.pending || this.sending) return;
    this.sending = true;
    this.request = new AbortController();
    try {
      const body = new URLSearchParams(new FormData(this.form));
      const response = await fetch(this.form.action, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        signal: this.request.signal,
      });
      const destination = new URL(response.url);
      if (destination.origin === location.origin && /\/challenge\/?$/.test(destination.pathname)) {
        location.assign(response.url);
        return;
      }
      if (!response.ok) throw new Error();
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = document.getElementById(this.form.id);
      if (!result?.querySelector('[data-newsletter-success]')) {
        throw new Error(result?.querySelector('[data-newsletter-error]:not([hidden])')?.textContent.trim());
      }
      const status = this.querySelector('[data-newsletter-status]');
      status.firstElementChild.textContent = result.querySelector('[data-newsletter-status]').textContent.trim();
      status.setAttribute('data-newsletter-success', '');
      status.classList.replace('sr-only', 'footer-confirmation');
      this.querySelector('[data-newsletter-controls]').hidden = true;
      this.form.querySelector('[type=email]').readOnly = true;
    } catch (error) {
      if (this.abort) {
        this.fail(error instanceof TypeError || error.name === 'AbortError' ? undefined : error.message);
      }
    } finally {
      clearTimeout(this.timeout);
      this.pending = false;
      this.sending = false;
      this.form.removeAttribute('aria-busy');
    }
  };

  setBusy(busy) {
    const button = this.querySelector('[data-newsletter-submit]');
    const label = this.querySelector('[data-newsletter-label]');
    this.originalLabel ??= label.textContent;
    button.disabled = busy;
    label.textContent = busy ? button.dataset.pendingLabel : this.originalLabel;
    if (busy) this.form.setAttribute('aria-busy', 'true');
    else this.form.removeAttribute('aria-busy');
  }

  fail(message) {
    clearTimeout(this.timeout);
    this.pending = false;
    this.setBusy(false);
    const error = this.querySelector('[data-newsletter-error]');
    error.textContent = message || error.dataset.fallback;
    error.hidden = false;
  }
}

if (!customElements.get('newsletter-form')) customElements.define('newsletter-form', NewsletterForm);
